import { createContext, useContext, useEffect } from 'react';

// Contexto para manejar personalización por género
const GenderContext = createContext();

export const useGender = () => {
  const context = useContext(GenderContext);
  if (!context) {
    return {
      gender: null,
      isMale: false,
      isFemale: true, // Default femenino ya que es app para mujeres
      getText: (maleText, femaleText) => femaleText,
      getColor: (maleColor, femaleColor) => femaleColor,
      theme: {
        primary: '#ec4899', // pink-500
        primaryDark: '#db2777', // pink-600
        primaryLight: '#f9a8d4', // pink-300
        gradient: 'from-pink-400 to-pink-600',
        gradientLight: 'from-pink-50 to-pink-100',
        text: 'text-pink-600',
        bg: 'bg-pink-500',
        bgLight: 'bg-pink-50',
        border: 'border-pink-500',
        hover: 'hover:bg-pink-600',
      }
    };
  }
  return context;
};

export const GenderProvider = ({ children, user }) => {
  const gender = user?.gender;
  const isMale = gender === 'male';
  // Solo marcar como femenino si explícitamente está configurado o si el usuario tiene género definido
  const isFemale = gender === 'female';
  // Por defecto (sin usuario o sin género), asumir femenino para la UI pero NO para bloqueo de contenido
  const hasGenderSet = !!gender;

  // Función para obtener texto personalizado
  const getText = (maleText, femaleText) => {
    return isMale ? maleText : femaleText;
  };

  // Función para obtener color personalizado
  const getColor = (maleColor, femaleColor) => {
    return isMale ? maleColor : femaleColor;
  };

  // Tema de colores según género
  const theme = isMale ? {
    primary: '#2563eb', // blue-600
    primaryDark: '#1d4ed8', // blue-700
    primaryLight: '#93c5fd', // blue-300
    gradient: 'from-blue-400 to-blue-600',
    gradientLight: 'from-blue-50 to-blue-100',
    text: 'text-blue-600',
    bg: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    border: 'border-blue-500',
    hover: 'hover:bg-blue-600',
    ring: 'ring-blue-500',
  } : {
    primary: '#ec4899', // pink-500
    primaryDark: '#db2777', // pink-600
    primaryLight: '#f9a8d4', // pink-300
    gradient: 'from-pink-400 to-pink-600',
    gradientLight: 'from-pink-50 to-pink-100',
    text: 'text-pink-600',
    bg: 'bg-pink-500',
    bgLight: 'bg-pink-50',
    border: 'border-pink-500',
    hover: 'hover:bg-pink-600',
    ring: 'ring-pink-500',
  };

  const value = {
    gender,
    isMale,
    isFemale,
    hasGenderSet, // Indica si el usuario tiene género configurado
    getText,
    getColor,
    theme,
  };

  // ═════ APLICAR CLASE CSS AL <html> SEGÚN GÉNERO ═════
  // Todo el CSS con `.theme-male` (index.css) se activa automáticamente.
  // Los hex hardcodeados ya usan var(--brand-primary) que apunta a azul cuando .theme-male.
  useEffect(() => {
    const root = document.documentElement;
    if (isMale) {
      root.classList.add('theme-male');
      root.classList.remove('theme-female');
    } else {
      // Default = femenino (rosa)
      root.classList.add('theme-female');
      root.classList.remove('theme-male');
    }
  }, [isMale]);

  return (
    <GenderContext.Provider value={value}>
      {children}
    </GenderContext.Provider>
  );
};

export default GenderContext;
